<script lang="ts">
  import { clamp } from "@/libs/math"
  import type { HTMLAttributes } from "svelte/elements"
  import { type TransitionConfig } from "svelte/transition"

  let {
    value = 0,
    max = 1,
    transition = () => ({}),
    ...props
  }: {
    value?: number
    max?: number
    transition?: (node: Element) => TransitionConfig
  } & HTMLAttributes<HTMLProgressElement> = $props()

  // By computing the progress as a percentage string, we ensure browsers like Safari
  // receive a valid unit (e.g., "0%", "50%", "100%") for the width, avoiding rendering issues.
  let progressPercentage = $derived(`${clamp(value ?? 0, { min: 0, max }) * 100}%`)
</script>

<progress
  {...props}
  class={[
    "relative h-1 w-full appearance-none overflow-hidden rounded-full backdrop-blur-2xl",
    "before:absolute before:inset-0 before:bg-current before:opacity-40",
    props.class,
  ]}
  style:--progress-width={progressPercentage}
  {value}
  {max}
  transition:transition
></progress>

<style lang="postcss">
  progress {
    &::-webkit-progress-bar {
      @apply bg-transparent;
    }
    /** Chrome, Safari, and other WebKit browsers */
    &::-webkit-progress-value {
      /* Use the CSS variable with proper unit to set the width.
         This ensures that Safari renders the progress fill correctly,
         and the transition on width is smooth. */
      width: var(--progress-width);
      transition: width 0.3s ease;
      @apply h-full rounded-2xl bg-current;
    }
    /** Firefox */
    &::-moz-progress-bar {
      width: var(--progress-width);
      transition: width 0.3s ease;
      @apply h-full rounded-2xl bg-current;
    }
  }
</style>
